import { isDefined, isNil } from "@clipboard-health/util-ts";
import {
  cognitoSignOut,
  getCognitoIdToken,
  hasStoredCognitoSession,
} from "@src/appV2/Auth/cognito/cognitoAuth";
import { isNetworkFailure } from "@src/appV2/Auth/loginRum";
import { redirectToLogin } from "@src/appV2/Auth/redirectToLogin";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { STEP_UP_REQUIRED_ERROR } from "@src/appV2/StepUp/constants";
import { requestStepUp, type StepUpMethod } from "@src/appV2/StepUp/stepUpController";
import axios, { type InternalAxiosRequestConfig } from "axios";

import { apiClient } from "./apiClient";
import { markUncertainNonGetTransportTimeout, replayTimedOutGet } from "./apiTransportRecovery";

function hasAuthorizationHeader(config: InternalAxiosRequestConfig | undefined): boolean {
  const authHeader = config?.headers?.Authorization;
  return isDefined(authHeader) && authHeader !== "";
}

async function recoverFromRevokedSession(source: string): Promise<never> {
  try {
    await cognitoSignOut();
  } catch (signOutError: unknown) {
    logError(APP_V2_APP_EVENTS.AUTH_AUTO_SIGN_OUT_FAILED, { error: signOutError });
  }
  redirectToLogin(source);
  return new Promise<never>(() => {});
}

export async function handleApiResponseError(error: unknown): Promise<unknown> {
  if (!axios.isAxiosError(error)) {
    if (error instanceof Error) {
      throw error;
    }
    return;
  }

  const { config } = error;
  const status = error.response?.status;
  const transportReplayResponse = await replayTimedOutGet({
    error,
    replayRequest: (retryConfig) => apiClient.request(retryConfig),
  });

  if (isDefined(transportReplayResponse)) {
    return transportReplayResponse;
  }

  markUncertainNonGetTransportTimeout({ error });

  if (status === 401 && !config?.skipAuthRedirectOnUnauthorized) {
    if (isNil(config) || !hasStoredCognitoSession()) {
      redirectToLogin("apiClient");
      return new Promise<never>(() => {});
    }

    if (config.authRetried) {
      logEvent(APP_V2_APP_EVENTS.API_401_RETRY_FAILED, {
        from: globalThis.location?.pathname,
        reason: "unauthorized_after_force_refresh",
      });
      throw error;
    }

    logEvent(APP_V2_APP_EVENTS.API_401_RETRY_WITH_FORCE_REFRESH, {
      from: globalThis.location?.pathname,
    });

    let forceRefreshSucceeded = false;
    try {
      const refreshedToken = await getCognitoIdToken(true);
      forceRefreshSucceeded = Boolean(refreshedToken);
    } catch {
      forceRefreshSucceeded = false;
    }

    if (!forceRefreshSucceeded) {
      logEvent(APP_V2_APP_EVENTS.API_401_RETRY_FAILED, {
        from: globalThis.location?.pathname,
        reason: "no_token_after_force_refresh",
      });
      redirectToLogin("apiClient");
      return new Promise<never>(() => {});
    }

    // Drop the stale header so the request interceptor re-fetches and the
    // `Authorization`-present branch doesn't flip `skipAuthRedirectOnUnauthorized`.
    config.authRetried = true;
    config.headers?.delete?.("Authorization");

    try {
      const retryResponse = await apiClient.request(config);
      logEvent(APP_V2_APP_EVENTS.API_401_RETRY_SUCCEEDED, {
        from: globalThis.location?.pathname,
      });
      return retryResponse;
    } catch (retryError: unknown) {
      if (!isUnauthorizedAfterForceRefreshError(retryError)) {
        logEvent(APP_V2_APP_EVENTS.API_401_RETRY_FAILED, {
          from: globalThis.location?.pathname,
          reason: "retry_request_failed",
        });
      }
      throw retryError;
    }
  }

  // CORS-stripped 4xx responses surface as ERR_NETWORK with no response;
  // probe Cognito to distinguish a revoked session from a real outage.
  if (
    error.code === "ERR_NETWORK" &&
    !config?.skipAuthRedirectOnUnauthorized &&
    isDefined(config) &&
    !config.authRetried &&
    hasAuthorizationHeader(config)
  ) {
    config.authRetried = true;

    logEvent(APP_V2_APP_EVENTS.API_NETWORK_ERROR_PROBE_INITIATED, {
      from: globalThis.location?.pathname,
    });

    let sessionConfirmedDead = false;
    try {
      const probeToken = await getCognitoIdToken(true);
      sessionConfirmedDead = !isDefined(probeToken);
    } catch (probeError: unknown) {
      if (isNetworkFailure(probeError)) {
        throw error;
      }
      sessionConfirmedDead = true;
    }

    if (sessionConfirmedDead) {
      logEvent(APP_V2_APP_EVENTS.API_NETWORK_ERROR_SESSION_REVOKED, {
        from: globalThis.location?.pathname,
      });
      return recoverFromRevokedSession("apiClientNetworkError");
    }
  }

  // PHI viewing access timeout (HIPAA §164.312(a)(1)). home-health-api returns a 403
  // JSON:API error with code STEP_UP_REQUIRED when the caller's PHI access has gone
  // stale. Drive the step-up modal, and on successful re-auth retry the original request
  // once. On cancel, re-throw so the caller can render a soft-blocked/empty state (no
  // logout).
  const responseBody = error.response?.data as
    | { errors?: Array<{ code?: string; meta?: { stepUpMethod?: StepUpMethod } }> }
    | undefined;
  const responseErrors = responseBody?.errors;
  const stepUpError = Array.isArray(responseErrors)
    ? responseErrors.find((jsonApiError) => jsonApiError.code === STEP_UP_REQUIRED_ERROR)
    : undefined;
  if (status === 403 && isDefined(stepUpError) && isDefined(config)) {
    const stepUpConfig = config as InternalAxiosRequestConfig & { stepUpRetried?: boolean };
    if (stepUpConfig.stepUpRetried) {
      throw error;
    }

    const reAuthenticated = await requestStepUp({
      stepUpMethod: stepUpError.meta?.stepUpMethod ?? "magic_link",
    });
    if (!reAuthenticated) {
      throw error;
    }

    stepUpConfig.stepUpRetried = true;
    // Drop the stale header so the request interceptor injects the fresh token.
    stepUpConfig.headers?.delete?.("Authorization");
    return apiClient.request(stepUpConfig);
  }

  throw error;
}

function isUnauthorizedAfterForceRefreshError(error: unknown): boolean {
  return (
    axios.isAxiosError(error) &&
    error.response?.status === 401 &&
    error.config?.authRetried === true
  );
}
